import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type NotificationRow = {
  id: string;
  user_id: string | null;
  type: "campaign_status" | "booking_status" | "payment" | "invoice" | "announcement" | "system";
  title: string;
  body: string | null;
  link: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

export type NotificationItem = NotificationRow & { read: boolean };

const PAGE_SIZE = 30;

export const useNotifications = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const [{ data: notifs }, { data: reads }] = await Promise.all([
        supabase
          .from("notifications")
          .select("*")
          .or(`user_id.eq.${user.id},user_id.is.null`)
          .order("created_at", { ascending: false })
          .limit(PAGE_SIZE),
        supabase
          .from("notification_reads")
          .select("notification_id")
          .eq("user_id", user.id),
      ]);
      const readSet = new Set((reads ?? []).map((r) => r.notification_id));
      setItems(
        ((notifs ?? []) as NotificationRow[]).map((n) => ({
          ...n,
          read: readSet.has(n.id),
        })),
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime: listen for new notifications targeting this user or broadcast
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("notifications-stream")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        (payload) => {
          const row = payload.new as NotificationRow;
          if (row.user_id && row.user_id !== user.id) return; // not for me
          setItems((prev) => [{ ...row, read: false }, ...prev].slice(0, PAGE_SIZE));
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const unreadCount = useMemo(() => items.filter((i) => !i.read).length, [items]);

  const markRead = useCallback(
    async (id: string) => {
      if (!user) return;
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      await supabase
        .from("notification_reads")
        .upsert({ user_id: user.id, notification_id: id }, { onConflict: "user_id,notification_id" });
    },
    [user],
  );

  const markAllRead = useCallback(async () => {
    if (!user) return;
    const unread = items.filter((n) => !n.read);
    if (unread.length === 0) return;
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    await supabase
      .from("notification_reads")
      .upsert(
        unread.map((n) => ({ user_id: user.id, notification_id: n.id })),
        { onConflict: "user_id,notification_id" },
      );
  }, [items, user]);

  return { items, loading, unreadCount, markRead, markAllRead, refresh: load };
};
